"use client";

/**
 * DiagnosticsPageClient — orchestrates the full diagnostics page:
 *   - New tab:   catalogue grid → AssessmentFlow modal → questions page
 *   - Saved tab: SavedDiagnosticsList
 *   - Custom diagnostic: AI query bar → questions generator API → session creation
 */
import { useState, useCallback } from "react";
import Link from "next/link";
import { Lock } from "lucide-react";
import { DiagnosticsHero }        from "@/shared/components/ui/DiagnosticsHero";
import { GeneratingModal }        from "@/shared/components/ui/GeneratingModal";
import { DiagnosticsGrid }        from "@/shared/components/ui/DiagnosticsGrid";
import { SavedDiagnosticsList }   from "@/modes/diagnostics/components/SavedDiagnosticsList";
import { useDiagnosticsHistory }  from "@/modes/diagnostics/hooks/useDiagnosticsHistory";
import { useAssessment }          from "@/modes/diagnostics/hooks/useAssessment";
import { useAuth }                from "@/platform/auth/useAuth";
import { AuthPromptModal }        from "@/modes/draft/components/shared/AuthPromptModal";
import { countUserDiagnostics }   from "@/platform/firebase/firestore/diagnostics";
import { planToTier }             from "@/shared/types";

export function DiagnosticsPageClient() {
  const { user, profile, loading: authLoading }              = useAuth();
  const { sessions, loading, error, refresh, toggleStar, toggleArchive, deleteRun } = useDiagnosticsHistory();
  const { startCustom }                                       = useAssessment();
  const [activeTab, setActiveTab]                             = useState<"new" | "saved">("new");
  const [queryLoading, setQueryLoading]                       = useState(false);
  const [currentQuery, setCurrentQuery]                       = useState("");
  const [customBanner, setCustomBanner]                       = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal]                     = useState(false);
  const [limitReached, setLimitReached]                       = useState(false);

  // ── Custom diagnostic query ──────────────────────────────────────────────────
  const handleQuerySubmit = useCallback(async (query: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!authLoading && profile) {
      if (profile.systemRole === "superadmin") {
        // superadmins bypass all plan limits
      } else {
        const tier = planToTier(profile.subscription?.plan ?? "free");
        if (tier === "free") {
          const count = await countUserDiagnostics(user.uid);
          if (count >= 1) {
            setLimitReached(true);
            return;
          }
        }
      }
    }
    setQueryLoading(true);
    setCustomBanner(null);
    setCurrentQuery(query);
    try {
      const customDiagnostic = {
        id: `custom-${Date.now()}`,
        title: `${query} Diagnostic`,
        tag: "Custom",
        desc: `Assess ${query.toLowerCase()} maturity and capabilities`,
        dom: "supplychain" as const,
        icon: "supplychain" as const,
        data: "dynamic" as const,
      };
      await startCustom(customDiagnostic);
      // startCustom navigates away on success — nothing more needed here
    } catch {
      setCustomBanner("Could not generate custom diagnostic right now. Please try a preset below.");
      setQueryLoading(false);
    }
  }, [startCustom, user, profile, authLoading]);

  return (
    <>
      {showAuthModal && (
        <AuthPromptModal onDismiss={() => setShowAuthModal(false)} />
      )}

      {queryLoading && <GeneratingModal query={currentQuery} />}

      {limitReached && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setLimitReached(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <Lock size={36} className="mx-auto mb-4 text-indigo-500" />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Free Plan Limit Reached</h3>
            <p className="text-sm text-gray-600 mb-6">
              Free users can create 1 diagnostic. Upgrade to Pro for unlimited diagnostics.
            </p>
            <Link
              href="/pricing"
              className="inline-block px-6 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#5B6CFF] to-[#EC4899] hover:from-[#4B5CEF] hover:to-[#D94185] transition-all"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>
      )}

      <DiagnosticsHero
        savedCount={sessions.filter((s) => !s.isArchived).length}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (tab === "saved") refresh();
        }}
        onQuerySubmit={handleQuerySubmit}
        queryLoading={queryLoading}
        isAuthenticated={!!user}
      />

      {customBanner && (
        <div
          role="status"
          style={{
            maxWidth: "800px",
            margin: "0 auto 24px",
            padding: "12px 20px",
            background: "rgba(239,68,68,0.06)",
            border: "1px solid rgba(239,68,68,0.2)",
            borderRadius: "10px",
            fontSize: "14px",
            color: "var(--ink)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {customBanner}
          <button
            onClick={() => setCustomBanner(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-mute)", fontSize: "16px", marginLeft: 16 }}
          >
            ✕
          </button>
        </div>
      )}

      {activeTab === "new" && <DiagnosticsGrid />}

      {activeTab === "saved" && (
        <div className="" style={{ maxWidth: "960px", margin: "0 auto", padding: "0 24px 80px" }}>
          {!user ? (
            <div style={{ maxWidth: "600px", margin: "32px auto 32px", padding: "0 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderRadius: 16, border: "1px solid rgba(91,108,255,0.3)", background: "rgba(91,108,255,0.08)", width: "100%" }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 12, background: "rgba(91,108,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }} aria-hidden="true">
                  🔒
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Sign in to view saved diagnostics</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.7 }}>
                    Create a free account or sign in to save and access your diagnostic results.
                  </p>
                </div>
                <a
                  href={`/login?redirect_to=${encodeURIComponent("/diagnostics")}`}
                  style={{ flexShrink: 0, padding: "8px 18px", borderRadius: 10, background: "linear-gradient(135deg,#5B6CFF,#EC4899)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
                >
                  Sign in
                </a>
              </div>
            </div>
          ) : (
            <SavedDiagnosticsList
              sessions={sessions}
              loading={loading}
              error={error}
              onDelete={deleteRun}
              onToggleStar={toggleStar}
              onToggleArchive={toggleArchive}
              onRefresh={refresh}
            />
          )}
        </div>
      )}
    </>
  );
}
